import { useEffect, useId, useState } from "react";
import { useQuery } from "@connectrpc/connect-query";

import { formatNepalTime } from "@yatayat/core/date";
import { formatNumber } from "@yatayat/core/money";
import { DemoService } from "@yatayat/gen/yatayat/demo/v1/demo_pb";

import { useI18n } from "~/lib/i18n";
import { localDigits } from "~/lib/labels";

const seenKey = "yatayat.sms.seenAtMs";

// The sign-in code in the api's OTP text (domain/passenger.go); the load test reads it the same way.
const otpCode = /code: (\d{6})/;

// Storage can throw in private windows; then nothing counts as read across reloads.
function readSeen(): number | undefined {
  try {
    const ms = Number(localStorage.getItem(seenKey));
    return ms > 0 ? ms : undefined;
  } catch {
    return undefined;
  }
}

function writeSeen(ms: number) {
  try {
    localStorage.setItem(seenKey, String(ms));
  } catch {
    // Not remembering is fine.
  }
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // The Clipboard API needs https or localhost, which a phone on the LAN may not have.
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    area.remove();
    return ok;
  }
}

/** Copies a sign-in code, so a visitor trying the demo on a phone can paste it. */
function CopyCode({ code }: { code: string }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      onClick={() => void copyText(code).then(setCopied)}
      className="mt-2 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-semibold hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-900 dark:hover:bg-slate-800"
    >
      <span aria-live="polite">{copied ? t("smsCopied") : t("smsCopyCode", { code })}</span>
    </button>
  );
}

/**
 * Shows what the mock SMS adapter "sent", so a demo can read sign-in codes without
 * a phone. Renders nothing unless the server's outbox answers. The button counts
 * messages that came in since the panel was last open.
 */
export function SmsOutbox() {
  const { t, lang } = useI18n();
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const [seenAtMs, setSeenAtMs] = useState(readSeen);
  // While the panel is open, messages newer than this are marked as new.
  const [newAfterMs, setNewAfterMs] = useState(Infinity);

  const outbox = useQuery(
    DemoService.method.listSmsOutbox,
    { limit: 20 },
    // Polls only where the outbox exists (demo mode), faster while it is open.
    { retry: false, staleTime: 0, refetchInterval: (q) => (q.state.data ? (open ? 3000 : 5000) : false) },
  );

  const messages = [...(outbox.data?.messages ?? [])].sort((a, b) => Number(b.sentAtMs - a.sentAtMs));
  const newestMs = messages.length > 0 ? Number(messages[0].sentAtMs) : 0;

  const markSeen = (ms: number) => {
    setSeenAtMs(ms);
    writeSeen(ms);
  };

  // A first visit counts what is already there as read; an open panel reads what arrives.
  useEffect(() => {
    if (!outbox.data) return;
    if (seenAtMs === undefined || (open && newestMs > seenAtMs)) markSeen(newestMs);
  }, [outbox.data, open, newestMs, seenAtMs]);

  // Data survives later failed refetches, so only a failed first call hides the button.
  if (!outbox.data) return null;

  const unread = seenAtMs === undefined || open ? 0 : messages.filter((m) => Number(m.sentAtMs) > seenAtMs).length;

  return (
    <div
      className="fixed bottom-3 left-3 z-40 flex flex-col items-start gap-2"
      onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
    >
      {open && (
        <section
          id={panelId}
          aria-label={t("smsTitle")}
          className="max-h-[60vh] w-[min(22rem,calc(100vw-1.5rem))] overflow-y-auto rounded-xl border border-slate-200 bg-white p-3 shadow-lg dark:border-slate-700 dark:bg-slate-900"
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold">{t("smsTitle")}</h2>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded px-2 py-0.5 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              {t("close")}
            </button>
          </div>
          {messages.length === 0 ? (
            <p className="text-sm text-slate-600 dark:text-slate-400">{t("smsEmpty")}</p>
          ) : (
            <ul aria-live="polite" className="space-y-2">
              {messages.map((m) => {
                const isNew = Number(m.sentAtMs) > newAfterMs;
                const code = otpCode.exec(m.body)?.[1];
                return (
                  <li
                    key={`${m.sentAtMs}:${m.toMasked}:${m.body}`}
                    className={`rounded-lg p-2 text-sm ${isNew ? "bg-amber-50 ring-1 ring-amber-300 dark:bg-amber-950 dark:ring-amber-800" : "bg-slate-50 dark:bg-slate-800"}`}
                  >
                    <p className="flex justify-between gap-2 text-xs text-slate-500 dark:text-slate-400">
                      <span>
                        {localDigits(m.toMasked, lang)}
                        {isNew && <span className="ml-2 font-semibold text-amber-800 dark:text-amber-300">{t("smsNewTag")}</span>}
                      </span>
                      <time dateTime={new Date(Number(m.sentAtMs)).toISOString()}>{formatNepalTime(Number(m.sentAtMs), lang)}</time>
                    </p>
                    <p className="mt-1 whitespace-pre-wrap break-words">{m.body}</p>
                    {code && <CopyCode code={code} />}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => {
          if (!open) {
            void outbox.refetch();
            setNewAfterMs(seenAtMs ?? Infinity);
          }
          setOpen(!open);
        }}
        className={`relative inline-flex items-center gap-1.5 rounded-full border bg-white px-3 py-1 text-xs font-semibold shadow hover:bg-slate-100 dark:bg-slate-900 dark:hover:bg-slate-800 ${
          unread > 0 ? "border-ink dark:border-ink-dark" : "border-slate-300 dark:border-slate-700"
        }`}
      >
        {t("smsButton")}
        {unread > 0 && (
          <>
            <span className="rounded-full bg-ink px-1.5 text-[11px] leading-4 text-white dark:bg-ink-dark dark:text-slate-950">
              {formatNumber(unread, lang)}
              <span className="sr-only"> {t("smsUnread")}</span>
            </span>
            {/* A soft pulse so a code arriving while the demo talks is noticed. */}
            <span aria-hidden="true" className="absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5">
              <span className="absolute h-full w-full rounded-full bg-ink opacity-75 motion-safe:animate-ping dark:bg-ink-dark" />
              <span className="relative h-2.5 w-2.5 rounded-full bg-ink dark:bg-ink-dark" />
            </span>
          </>
        )}
      </button>
    </div>
  );
}
